// One TRANSPARENT overlay track per 4K group, 3840x2160 @ 30.
// Every overlay is placed at its GROUP-relative time, derived from the clip it belongs to.
//   kind "name"  -> SCPersonTag    (bottom LEFT)
//   kind "place" -> SCLocationTag  (bottom RIGHT)
//   kind "type"  -> typewriter, centred, opening card only
import { AbsoluteFill, useCurrentFrame, useVideoConfig, interpolate } from "remotion";
import { loadFont } from "@remotion/google-fonts/Inter";
import { loadFont as loadDev } from "@remotion/google-fonts/NotoSansDevanagari";
import { SCPersonTag, SCLocationTag, SC_FPS } from "./SanatanCinematicsTags";

const { fontFamily } = loadFont("normal", { weights: ["600", "700"], subsets: ["latin"] });
const { fontFamily: devFont } = loadDev("normal", { weights: ["400"], subsets: ["devanagari"] });

export type SCOverlay = {
  kind: "name" | "place" | "type";
  name: string;
  name_hindi?: string;
  t_in: number;      // seconds into THIS GROUP
  t_out: number;     // seconds into THIS GROUP
};
export type SCGroupProps = { piece?: string; lengthSeconds?: number; overlays?: SCOverlay[] };

/** Typewriter: the Latin line types, then the Devanagari types under it. */
const TypeCard: React.FC<{ o: SCOverlay }> = ({ o }) => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();
  const s = Math.round(o.t_in * fps), e = Math.round(o.t_out * fps);
  const f = frame - s;
  if (f < 0 || f >= e - s) return null;
  const total = e - s;
  const perChar = 1.6;                                   // frames per character
  const nLat = Math.max(0, Math.min(o.name.length, Math.floor(f / perChar)));
  const hiStart = o.name.length * perChar + 6;
  const nDev = o.name_hindi
    ? Math.max(0, Math.min(o.name_hindi.length, Math.floor((f - hiStart) / perChar)))
    : 0;
  const alpha = interpolate(f, [0, 4, total - Math.round(0.5 * fps), total],
    [0, 1, 1, 0], { extrapolateLeft: "clamp", extrapolateRight: "clamp" });
  const caret = Math.floor(f / 8) % 2 === 0;
  return (
    <AbsoluteFill style={{ opacity: alpha, pointerEvents: "none",
                           alignItems: "center", justifyContent: "center" }}>
      <div style={{ textAlign: "center" }}>
        <div style={{ fontFamily, fontSize: 84, fontWeight: 700, color: "#fff",
                      letterSpacing: 6, textTransform: "uppercase" }}>
          {o.name.slice(0, nLat)}{nLat < o.name.length && caret ? "_" : ""}
        </div>
        {o.name_hindi && (
          <div style={{ fontFamily: devFont, fontSize: 76, fontWeight: 400,
                        color: "rgba(255,255,255,0.82)", marginTop: 18 }}>
            {o.name_hindi.slice(0, nDev)}
            {nDev > 0 && nDev < o.name_hindi.length && caret ? "_" : ""}
          </div>
        )}
      </div>
    </AbsoluteFill>
  );
};

export const SanatanGroupOverlay: React.FC<SCGroupProps> = ({ overlays = [] }) => {
  const { fps } = useVideoConfig();
  return (
    <AbsoluteFill>
      {overlays.map((o, i) => {
        const start = Math.round(o.t_in * fps);
        const dur = Math.max(1, Math.round((o.t_out - o.t_in) * fps));
        if (o.kind === "type") return <TypeCard key={i} o={o} />;
        const T = o.kind === "place" ? SCLocationTag : SCPersonTag;
        return <T key={i} data={{ name: o.name, name_hindi: o.name_hindi }}
                  durationInFrames={dur} startFrame={start} />;
      })}
    </AbsoluteFill>
  );
};

/** durationInFrames is COMPUTED from the group length — never typed. */
export const scGroupMetadata = ({ props }: { props: SCGroupProps }) => ({
  durationInFrames: Math.max(1, Math.round((props.lengthSeconds ?? 10) * SC_FPS)),
});

const DEMO: SCGroupProps = {
  piece: "A1_G00_A_720p",
  lengthSeconds: 27.17,
  overlays: [
    { kind: "type",  name: "5000 YEARS AGO", name_hindi: "पाँच हज़ार साल पहले", t_in: 0.5,   t_out: 3.1 },
    { kind: "place", name: "MATHURA",        name_hindi: "मथुरा",                t_in: 3.2,   t_out: 5.8 },
    { kind: "name",  name: "KANSA",          name_hindi: "कंस",                  t_in: 10.68, t_out: 13.28 },
    { kind: "name",  name: "THE GODDESS",    name_hindi: "देवी",                 t_in: 16.76, t_out: 19.36 },
  ],
};
export const SCGroupDemo: React.FC = () => <SanatanGroupOverlay {...DEMO} />;
export const SC_GROUP_DEMO = DEMO;

;
    var _remotion_globalVariableA;
    var _remotion_globalVariableB;
    // Legacy CSS implementations will `eval` browser code in a Node.js context
    // to extract CSS. For backwards compatibility, we need to check we're in a
    // browser context before continuing.
    if (typeof self !== 'undefined' &&
        // AMP / No-JS mode does not inject these helpers:
        '$RefreshHelpers$' in self) {
        const currentExports = __webpack_module__.exports;
        const prevExports = (_remotion_globalVariableB = (_remotion_globalVariableA = __webpack_module__.hot.data) === null || _remotion_globalVariableA === void 0 ? void 0 : _remotion_globalVariableA.prevExports) !== null && _remotion_globalVariableB !== void 0 ? _remotion_globalVariableB : null;
        // This cannot happen in MainTemplate because the exports mismatch between
        // templating and execution.
        self.$RefreshHelpers$.registerExportsForReactRefresh(currentExports, __webpack_module__.id);
        // A module can be accepted automatically based on its exports, e.g. when
        // it is a Refresh Boundary.
        if (self.$RefreshHelpers$.isReactRefreshBoundary(currentExports)) {
            // Save the previous exports on update so we can compare the boundary
            // signatures.
            __webpack_module__.hot.dispose((data) => {
                data.prevExports = currentExports;
            });
            // Unconditionally accept an update to this module, we'll check if it's
            // still a Refresh Boundary later.
            __webpack_module__.hot.accept();
            // This field is set when the previous version of this module was a
            // Refresh Boundary, letting us know we need to check for invalidation or
            // enqueue an update.
            if (prevExports !== null) {
                // A boundary can become ineligible if its exports are incompatible
                // with the previous exports.
                //
                // For example, if you add/remove/change exports, we'll want to
                // re-execute the importing modules, and force those components to
                // re-render. Similarly, if you convert a class component to a
                // function, we want to invalidate the boundary.
                if (self.$RefreshHelpers$.shouldInvalidateReactRefreshBoundary(prevExports, currentExports)) {
                    __webpack_module__.hot.invalidate();
                }
                else {
                    self.$RefreshHelpers$.scheduleUpdate();
                }
            }
        }
        else {
            // Since we just executed the code for the module, it's possible that the
            // new exports made it ineligible for being a boundary.
            // We only care about the case when we were _previously_ a boundary,
            // because we already accepted this update (accidental side effect).
            const isNoLongerABoundary = prevExports !== null;
            if (isNoLongerABoundary) {
                __webpack_module__.hot.invalidate();
            }
        }
    }
